import { Button } from '@Pages/pageRuntime/components/ui/Button';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle
} from '@Pages/pageRuntime/components/ui/dialog/AlertDialog';
import {
  useRestoreResourceRecord,
  type RestoreResourceRecordResource
} from '@Pages/pageRuntime/hooks/useRestoreResourceRecord';
import { canPerformResourceOperation } from '@Pages/pageRuntime/sdk/authorization';
import type { SdkResource } from '@Pages/pageRuntime/sdk/types';
import React, { useState } from 'react';
import { toast } from 'sonner';

import { RecordRestoreEffectKind, RecordRestoreEffectKindUtils } from '@/types/enums';

type PreviewData = Diagonal.Data.Data.RecordRestorePreviewData;
type RecordPlanEffectData = Diagonal.Data.Data.RecordPlanEffectData;
type RecordPlanItemData = Diagonal.Data.Data.RecordPlanItemData;

type RestoreResourceRecordProps = {
  /**
   * Resource handle returned by `Sdk.resource('<resourceId>')`.
   */
  resource: RestoreResourceRecordResource & Pick<SdkResource, 'policy_scope'>;

  /**
   * Record ID to restore.
   */
  recordId: string | null | undefined;

  /**
   * Button label.
   */
  label?: string;

  /**
   * Heading shown in the confirmation dialog.
   */
  confirmTitle?: React.ReactNode;

  /**
   * Supporting text shown in the confirmation dialog.
   */
  confirmDescription?: React.ReactNode;

  /**
   * Called after a successful restoration, before navigation.
   */
  onSuccess?: () => void;

  /**
   * Optional container className.
   */
  className?: string;
};

/**
 * A resource-aware restore action with preview and confirmation behavior. Use for
 * restoring soft-deleted resource records instead of hand-built restore buttons.
 * After restoration, navigates to the resource records index.
 *
 * @example
 * const recordId = Sdk.helpers.useInput('record_id');
 * const students = Sdk.resource('students');
 *
 * return (
 *   <Sdk.Components.RestoreResourceRecord
 *     resource={students}
 *     recordId={recordId}
 *     label="Restore student"
 *   />
 * );
 */
export function RestoreResourceRecord({
  resource,
  recordId,
  label,
  confirmTitle,
  confirmDescription,
  onSuccess,
  className
}: RestoreResourceRecordProps) {
  const resourceId = resource?.id;
  const [isOpen, setIsOpen] = useState(false);
  const { preview, isPreviewLoading, isPreviewError, confirm, isProcessing, restoreError, reset } =
    useRestoreResourceRecord({
      resource,
      recordId: recordId ?? undefined,
      fetchPreview: isOpen,
      onSuccess: () => {
        setIsOpen(false);
        // The restore API flow is client-driven and never round-trips through the
        // server's flash message, so surface the success toast here.
        toast.success('Record restored.');
        onSuccess?.();
      }
    });

  function handleOpenChange(nextOpen: boolean): void {
    setIsOpen(nextOpen);
    if (!nextOpen) {
      reset();
    }
  }

  function handleConfirm(event: React.MouseEvent<HTMLButtonElement>): void {
    // Stop Radix's default action handler so the dialog stays open while the
    // restore request runs and so any error response can render.
    event.preventDefault();
    confirm();
  }

  if (!resourceId || !recordId) {
    return (
      <Button disabled className={className}>
        {label ?? 'Restore'}
      </Button>
    );
  }

  if (!canPerformResourceOperation(resource, 'restore')) {
    return null;
  }

  return (
    <>
      <Button onClick={() => setIsOpen(true)} className={className}>
        {label ?? 'Restore'}
      </Button>
      <AlertDialog open={isOpen} onOpenChange={handleOpenChange}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{confirmTitle ?? 'Restore record'}</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <RestoreDialogDescription
                confirmDescription={confirmDescription}
                isPreviewLoading={isPreviewLoading}
                isPreviewError={isPreviewError}
                preview={preview}
                hasError={Boolean(restoreError)}
              />
            </AlertDialogDescription>
          </AlertDialogHeader>

          {preview && preview.effects.length > 0 ? (
            <RestoreEffectsList effects={preview.effects} />
          ) : null}

          <AlertDialogFooter
            start={<AlertDialogCancel disabled={isProcessing}>Cancel</AlertDialogCancel>}
          >
            <AlertDialogAction onClick={handleConfirm} disabled={isProcessing || isPreviewLoading}>
              {label ?? 'Restore'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

function RestoreDialogDescription({
  confirmDescription,
  isPreviewLoading,
  isPreviewError,
  preview,
  hasError
}: {
  confirmDescription: React.ReactNode;
  isPreviewLoading: boolean;
  isPreviewError: boolean;
  preview: PreviewData | undefined;
  hasError: boolean;
}): React.ReactNode {
  const authoredDescription = confirmDescription ?? 'Are you sure?';

  return (
    <div className="space-y-2">
      {authoredDescription ? <p>{authoredDescription}</p> : null}

      {hasError ? (
        <p>Unable to restore this record. Try again.</p>
      ) : isPreviewLoading ? (
        <p>Checking related records...</p>
      ) : isPreviewError ? (
        <p>Unable to check for related records. Try again.</p>
      ) : preview ? (
        <p>{preview.summary}</p>
      ) : null}
    </div>
  );
}

function RestoreEffectsList({ effects }: { effects: RecordPlanEffectData[] }): React.ReactNode {
  return (
    <ul
      data-testid="restore-resource-record-effects"
      className="max-h-72 space-y-3 overflow-y-auto rounded-md border border-border bg-muted/30 p-3 text-sm"
    >
      {effects.map((effect) => (
        <li key={`${effect.kind}-${effect.field.id}`} className="space-y-1">
          <RestoreEffectHeader effect={effect} />
          {effect.items.length > 0 ? (
            <ul className="ml-4 list-disc space-y-0.5 text-muted-foreground">
              {effect.items.map((item) => (
                <RestoreEffectItem key={`${effect.field.id}-${item.id}`} item={item} />
              ))}
            </ul>
          ) : null}
        </li>
      ))}
    </ul>
  );
}

function RestoreEffectHeader({ effect }: { effect: RecordPlanEffectData }): React.ReactNode {
  const kindLabel = effectKindLabel(effect);

  return (
    <div className="flex flex-wrap items-baseline gap-1 font-medium">
      <span>{kindLabel}</span>
      <span>{effect.count}</span>
      <span>{effect.target.label ?? effect.target.resource}</span>
      <span className="text-muted-foreground">via {effect.field.label}</span>
    </div>
  );
}

function effectKindLabel(effect: RecordPlanEffectData): string {
  if (!RecordRestoreEffectKindUtils.isValid(effect.kind)) {
    return String(effect.kind);
  }

  const labels: Record<RecordRestoreEffectKind, string> = {
    [RecordRestoreEffectKind.Parent]: 'Will first restore',
    [RecordRestoreEffectKind.Cascade]: 'Will also restore'
  };

  return labels[effect.kind];
}

function RestoreEffectItem({ item }: { item: RecordPlanItemData }): React.ReactNode {
  return (
    <li>
      <span>{item.label ?? item.id}</span>
      {item.via ? (
        <span className="ml-1 text-xs italic">via {item.via.label ?? item.via.record}</span>
      ) : null}
    </li>
  );
}
